/**
 * SEO Component - Meta tags dynamiques pour chaque page
 * Utilise react-helmet-async pour le SSR-friendly SEO
 */
import { Helmet } from 'react-helmet-async';

const DEFAULT_TITLE = 'LEMO - Click & Collect';
const DEFAULT_DESCRIPTION = 'Commandez vos plats préférés et récupérez-les en restaurant. Click & Collect facile et rapide.';
const DEFAULT_IMAGE = '/icons/icon-512x512.png';
const SITE_URL = 'https://lemo-food.com';

export function SEO({ 
  title, 
  description = DEFAULT_DESCRIPTION, 
  image = DEFAULT_IMAGE,
  url,
  type = 'website',
  noindex = false,
  structuredData = null
}) {
  const fullTitle = title ? `${title} | LEMO` : DEFAULT_TITLE;
  const fullUrl = url ? `${SITE_URL}${url}` : SITE_URL;
  const fullImage = image?.startsWith('http') ? image : `${SITE_URL}${image}`;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      
      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={fullImage} />
      <meta property="og:locale" content="fr_FR" />
      <meta property="og:site_name" content="LEMO" />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullImage} />
      
      {/* Canonical */}
      <link rel="canonical" href={fullUrl} />

      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
}

// Pre-configured SEO for common pages
export const PageSEO = {
  Home: () => (
    <SEO 
      title="Accueil"
      description="LEMO - Commandez vos plats préférés en click & collect. Découvrez les meilleurs restaurants près de chez vous et récupérez votre commande rapidement."
      url="/"
      structuredData={{
        "@context": "https://schema.org",
        "@type": "WebSite",
        "name": "LEMO",
        "url": SITE_URL,
        "description": "Plateforme de click & collect pour restaurants",
        "potentialAction": {
          "@type": "SearchAction",
          "target": `${SITE_URL}/restaurants?q={search_term_string}`,
          "query-input": "required name=search_term_string"
        }
      }}
    />
  ),
  
  Restaurants: () => (
    <SEO 
      title="Restaurants"
      description="Parcourez notre sélection de restaurants. Commandez en click & collect et récupérez vos plats en restaurant."
      url="/restaurants"
    />
  ),
  
  Restaurant: ({ name, description, image, id, address, rating, reviewCount }) => (
    <SEO 
      title={name}
      description={description || `Commandez chez ${name} en click & collect sur LEMO.`}
      image={image}
      url={`/restaurant/${id}`}
      type="restaurant"
      structuredData={{
        "@context": "https://schema.org",
        "@type": "Restaurant",
        "name": name,
        "description": description || `${name} - Click & Collect sur LEMO`,
        "url": `${SITE_URL}/restaurant/${id}`,
        ...(image && { "image": image }),
        ...(address && { 
          "address": {
            "@type": "PostalAddress",
            "streetAddress": address
          }
        }),
        ...(rating && {
          "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": rating,
            "reviewCount": reviewCount || 1,
            "bestRating": 5
          }
        }),
        "servesCuisine": "Variée",
        "acceptsReservations": false,
        "hasOfferCatalog": {
          "@type": "OfferCatalog",
          "name": "Menu"
        }
      }}
    />
  ),
  
  Login: () => (
    <SEO 
      title="Connexion"
      description="Connectez-vous à votre compte LEMO pour commander vos plats préférés."
      url="/login"
      noindex={true}
    />
  ),
  
  Signup: () => (
    <SEO 
      title="Inscription"
      description="Créez votre compte LEMO et commencez à commander en click & collect."
      url="/signup"
    />
  ),
  
  Checkout: () => (
    <SEO 
      title="Finaliser la commande"
      noindex={true}
    />
  ),
  
  Profile: () => (
    <SEO 
      title="Mon profil"
      noindex={true}
    />
  ),

  Search: ({ query }) => (
    <SEO 
      title={query ? `Recherche: ${query}` : 'Rechercher un plat'}
      description={query ? `Résultats de recherche pour "${query}" sur LEMO` : 'Recherchez vos plats préférés sur LEMO'}
      url="/search"
      noindex={true}
    />
  ),
};

export default SEO;
